import PropTypes from 'prop-types';
import FormProvider, { RHFSelect, RHFInput, RHFTextarea, RHFInputCurrency } from 'Components/Organisms/HookForm';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMemo, useState } from 'react';
import Button from '../../Components/Atoms/Button/Button.jsx';
import { getCurrencySymbol } from '../../Utils/formatters.js';
import useLocale from '../../Hooks/useLocale.js';
import useCurrency from '../../Hooks/useCurrency.js';
import useInvoiceUnitsMeasurement from 'Hooks/useInvoiceUnitsMeasurement';
import RHFSelectMultipleTaxes from '../../Components/Organisms/HookForm/RHFSelectMultipleTaxes.jsx';
import { useTranslation } from 'react-i18next';

export default function ReferenceEditorDrawer(props) {
    const { reference, onSubmit, onCancel } = props;
    const [processing, setProcessing] = useState(false);

    const { t } = useTranslation('references');

    const referenceSchema = yup.object().shape({
        name: yup.string().max(128, t('fields-name-max')).required(t('fields-name-required')),
        description: yup.string().max(512, t('fields-description-max')).nullable(),
        type: yup
            .string()
            .oneOf(['product', 'service', 'resale'], t('fields-type-invalid'))
            .required(t('fields-type-required')),
        unit_price: yup
            .number()
            .typeError(t('fields-unit-price-type-error'))
            .nullable()
            .min(0, t('fields-unit-price-min-error')),
        unit_measurement: yup.string().required(t('fields-unit-measurement-required')),
    });

    const methods = useForm({
        resolver: yupResolver(referenceSchema),
        defaultValues: {
            name: reference.name ?? '',
            description: reference.description ?? '',
            type: reference.type ?? 'product',
            unit_price: reference.unit_price ?? 0,
            unit_measurement: reference.unit_measurement ?? 'unit',
            taxes_ids: reference.taxes_ids ?? [],
        },
    });

    const submit = async (data) => {
        setProcessing(true);

        const payload = {
            ...data,
            description: data.description?.trim() === '' ? null : data.description,
        };

        await onSubmit(payload);
        setProcessing(false);
    };

    const { handleSubmit } = methods;

    const locale = useLocale();
    const defaultCurrency = useCurrency();
    const currencySymbol = getCurrencySymbol(locale, defaultCurrency);
    const { invoiceUnitsMeasurement } = useInvoiceUnitsMeasurement();

    const typeOptions = [
        { value: 'product', label: t('fields-type-product') },
        { value: 'service', label: t('fields-type-service') },
        { value: 'resale', label: t('fields-type-resale') },
    ];

    const unitMeasurementOptions = useMemo(() => {
        return invoiceUnitsMeasurement.map((unit) => ({
            value: unit.code,
            label: unit.name,
        }));
    }, [invoiceUnitsMeasurement]);

    return (
        <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
            <div className="grow flex flex-col gap-6 pt-3 pb-6">
                <RHFInput name="name" label={t('fields-name')} data-autofocus="true" />

                <RHFTextarea name="description" label={t('fields-description')} />

                <RHFSelect name="type" label={t('fields-type')} options={typeOptions} className={'w-full'} />

                <RHFInputCurrency name="unit_price" label={t('fields-unit-price')} prefix={currencySymbol} />

                <RHFSelect
                    name="unit_measurement"
                    label={t('fields-unit-measurement')}
                    options={unitMeasurementOptions}
                    className={'w-full'}
                />

                <RHFSelectMultipleTaxes name="taxes_ids" label={t('taxes_ids')} className="w-full" />
            </div>

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button className="grow" onClick={onCancel} testId="reference-editor.cancel">
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        isBusy={processing}
                        type="submit"
                        className="grow"
                        testId="reference-editor.save"
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

ReferenceEditorDrawer.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    reference: PropTypes.object,
};
